import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { GALLERY } from "@/lib/content";
import { api } from "@/lib/api";

export default function Gallery() {
  const [index, setIndex] = useState(null);
  const [extra, setExtra] = useState([]);

  useEffect(() => {
    api
      .get("/gallery/photos")
      .then(({ data }) => setExtra(data || []))
      .catch(() => setExtra([]));
  }, []);

  const items = [
    ...GALLERY,
    ...extra.map((p) => ({
      src: p.data_url,
      alt: p.caption || "Guest photo",
      caption: p.caption || "From our kitchen",
      span: "md:col-span-3",
    })),
  ];

  const open = index !== null;
  const close = () => setIndex(null);
  const prev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === 0 ? items.length - 1 : i - 1));
  };
  const next = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === items.length - 1 ? 0 : i + 1));
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="chip">Gallery</span>
            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl max-w-3xl">
              A peek inside our{" "}
              <span className="italic font-light">kitchen</span> &{" "}
              <span className="text-[#E53935]">wok</span>.
            </h2>
          </div>
          <p className="max-w-sm text-[15px] text-[#5C534D] leading-relaxed">
            Click any photo to expand. Real food, plated in our tiny dining room
            above George Street.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[160px] md:auto-rows-[220px]">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`gallery-tile-${i}`}
              aria-label={`Open ${item.caption}`}
              className={`group relative overflow-hidden rounded-2xl ${item.span} col-span-1 ${i === 0 ? "row-span-2" : ""}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand className="h-4 w-4 text-[#1A1A1A]" />
              </div>
              <div className="absolute bottom-3 left-4 right-4 text-left text-white font-display font-bold text-sm md:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                {item.caption}
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-[94vw] md:max-w-5xl p-0 bg-black border-0 overflow-hidden rounded-2xl [&>button]:bg-white/95 [&>button]:h-11 [&>button]:w-11 [&>button]:rounded-full [&>button]:grid [&>button]:place-items-center [&>button]:right-4 [&>button]:top-4 [&>button]:opacity-100 [&>button]:shadow-lg [&>button_svg]:h-5 [&>button_svg]:w-5 [&>button_svg]:text-[#1A1A1A]"
        >
          {open && (
            <div className="relative">
              <img
                src={items[index].src}
                alt={items[index].alt}
                className="w-full max-h-[84vh] object-contain bg-black"
              />
              <button
                onClick={prev}
                data-testid="lightbox-prev"
                aria-label="Previous"
                className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 hover:bg-white grid place-items-center transition-colors"
              >
                <ChevronLeft className="h-6 w-6 text-[#1A1A1A]" />
              </button>
              <button
                onClick={next}
                data-testid="lightbox-next"
                aria-label="Next"
                className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 hover:bg-white grid place-items-center transition-colors"
              >
                <ChevronRight className="h-6 w-6 text-[#1A1A1A]" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black via-black/60 to-transparent">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623] font-bold">
                  {index + 1} / {items.length}
                </div>
                <div className="mt-1 text-white font-display font-bold text-lg">
                  {items[index].caption}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
